import { Star } from "lucide-react";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

type BaseFieldProps = {
  name: string;
  label?: string;
  required?: boolean;
  className?: string;
};

type TextFieldProps = BaseFieldProps & {
  type?: "text" | "email" | "password";
  placeholder?: string;
  autoComplete?: string;
};

type TextareaFieldProps = BaseFieldProps & {
  placeholder?: string;
  rows?: number;
};

type SelectFieldProps = BaseFieldProps & {
  placeholder?: string;
  options: { value: string; label: string }[];
};

type RadioFieldProps = BaseFieldProps & {
  options: { value: string; label: string }[];
  defaultValue?: string;
};

type RatingFieldProps = BaseFieldProps & {
  maxRating?: number;
  defaultValue?: number;
};

export function TextField({
  name,
  label = "",
  type = "text",
  placeholder,
  required = false,
  className = "",
  autoComplete,
}: TextFieldProps) {
  return (
    <div className="space-y-3">
      <Label
        htmlFor={name}
        className="text-sm uppercase tracking-wide text-gray-500"
      >
        {label}
      </Label>
      <Input
        type={type}
        name={name}
        placeholder={placeholder}
        id={name}
        required={required}
        autoComplete={autoComplete}
        className={`w-full h-12 border-0 border-b border-gray-300 bg-transparent placeholder:text-lg placeholder:text-gray-400 focus:border-black rounded-none ${className}`}
      />
    </div>
  );
}

export function TextareaField({
  name,
  label = "",
  placeholder,
  rows = 4,
  required = false,
  className = "",
}: TextareaFieldProps) {
  return (
    <div className="space-y-3">
      <Label
        htmlFor={name}
        className="text-sm uppercase tracking-wide text-gray-500"
      >
        {label}
      </Label>
      <Textarea
        name={name}
        placeholder={placeholder}
        id={name}
        rows={rows}
        required={required}
        className={`w-full border-0 border-b border-gray-300 bg-transparent placeholder:text-lg placeholder:text-gray-400 focus:border-black rounded-none resize-none ${className}`}
      />
    </div>
  );
}

export function SelectField({
  name,
  label = "",
  placeholder,
  options,
  required = false,
  className = "",
}: SelectFieldProps) {
  return (
    <div className="space-y-3 w-full">
      {label.length > 0 && (
        <Label
          htmlFor={name}
          className="text-sm uppercase tracking-wide text-gray-500"
        >
          {label}
        </Label>
      )}
      <Select name={name} required={required}>
        <SelectTrigger
          className={`w-full h-12 border-0 border-b border-gray-300 bg-transparent text-lg rounded-none focus:border-black ${className}`}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem
              key={option.value}
              className="text-lg"
              value={option.value}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function RadioField({
  name,
  label = "",
  options,
  required = false,
  defaultValue,
  className = "",
}: RadioFieldProps) {
  return (
    <div className="space-y-3">
      {label.length > 0 && (
        <Label className="text-sm uppercase tracking-wide text-gray-500">
          {label}
        </Label>
      )}
      <RadioGroup
        name={name}
        defaultValue={defaultValue}
        required={required}
        className={`flex items-center ${className}`}
      >
        {options.map((option) => (
          <div key={option.value} className="flex items-center space-x-2">
            <RadioGroupItem
              value={option.value}
              id={`${name}-${option.value}`}
            />
            <Label
              htmlFor={`${name}-${option.value}`}
              className="text-lg text-gray-500 font-normal cursor-pointer"
            >
              {option.label}
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}

export function RatingField({
  name,
  label = "",
  maxRating = 5,
  defaultValue = 0,
  required = false,
  className = "",
}: RatingFieldProps) {
  const [rating, setRating] = useState(defaultValue);

  const handleRatingChange = (newRating: number) => {
    setRating(newRating);
  };

  return (
    <div className="space-y-3">
      {label.length > 0 && (
        <Label className="text-sm uppercase tracking-wide text-gray-500">
          {label}
        </Label>
      )}
      <div className={`flex gap-1 ${className}`}>
        {Array.from({ length: maxRating }, (_, i) => i + 1).map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => handleRatingChange(star)}
            className="p-1 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 rounded"
          >
            <Star
              className={`h-6 w-6 transition-colors ${
                star <= rating
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-gray-300 hover:text-gray-400"
              }`}
            />
          </button>
        ))}
        <input type="hidden" name={name} value={rating} required={required} />
      </div>
    </div>
  );
}
